import {
  CanvasTexture,
  NearestFilter,
  SRGBColorSpace,
} from 'three';

const SIZE = 64;
/** Number of progressive crack stages (Minecraft-style). */
export const CRACK_STAGE_COUNT = 8;

/**
 * Progressive crack overlays that grow from the block center outward.
 * Same primary rays every stage — only length / forks increase, so damage
 * reads as one fracture pattern spreading, not random new cracks.
 */
export function createCrackStageTextures(): CanvasTexture[] {
  const textures: CanvasTexture[] = [];
  for (let stage = 0; stage < CRACK_STAGE_COUNT; stage++) {
    textures.push(buildCrackTexture(stage));
  }
  return textures;
}

/** Primary rays — always drawn; length scales with stage. */
const PRIMARY_RAYS = [
  0.0, // right
  Math.PI * 0.5, // down
  Math.PI, // left
  Math.PI * 1.5, // up
  Math.PI * 0.25, // diagonals
  Math.PI * 0.75,
  Math.PI * 1.25,
  Math.PI * 1.75,
];

function buildCrackTexture(stage: number): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, SIZE, SIZE);

  const cx = SIZE * 0.5;
  const cy = SIZE * 0.5;
  const t = (stage + 1) / CRACK_STAGE_COUNT;

  // Reach: stage 0 ~12% of half-size, stage 7 ~95% (near edges).
  const half = SIZE * 0.5;
  const reach = half * (0.18 + t * 0.78);
  const lineWidth = 1.6 + stage * 0.35;

  // How many primary rays are active (unlock gradually).
  const activePrimary = Math.min(PRIMARY_RAYS.length, 4 + Math.floor(stage * 0.6));

  for (let i = 0; i < activePrimary; i++) {
    const angle = PRIMARY_RAYS[i]!;
    // Cardinals grow fastest; diagonals lag slightly so the cross reads first.
    const lag = i < 4 ? 1 : 0.72 + t * 0.28;
    const length = reach * lag;
    drawRay(ctx, cx, cy, angle, length, lineWidth, stage, i, false);
  }

  // Secondary forks appear mid-damage, branching off primary tips.
  if (stage >= 3) {
    const forkCount = Math.min(activePrimary, 2 + (stage - 3));
    for (let i = 0; i < forkCount; i++) {
      const angle = PRIMARY_RAYS[i]!;
      const baseLen = reach * (i < 4 ? 1 : 0.75) * 0.55;
      const bx = cx + Math.cos(angle) * baseLen;
      const by = cy + Math.sin(angle) * baseLen;
      const forkA = angle + (i % 2 === 0 ? 0.55 : -0.55);
      const forkLen = reach * (0.22 + t * 0.2);
      drawRay(ctx, bx, by, forkA, forkLen, lineWidth * 0.65, stage, i + 20, true);
    }
  }

  // Center impact mark.
  ctx.fillStyle = `rgba(5,4,3,${0.55 + t * 0.35})`;
  ctx.beginPath();
  ctx.arc(cx, cy, 1.5 + stage * 0.4, 0, Math.PI * 2);
  ctx.fill();

  // Subtle center darkening so cracks pop on bright ores.
  const bruise = ctx.createRadialGradient(cx, cy, 0, cx, cy, reach * 0.55);
  bruise.addColorStop(0, `rgba(0,0,0,${0.06 + t * 0.14})`);
  bruise.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = bruise;
  ctx.fillRect(0, 0, SIZE, SIZE);

  const texture = new CanvasTexture(canvas);
  texture.magFilter = NearestFilter;
  texture.minFilter = NearestFilter;
  texture.colorSpace = SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function drawRay(
  ctx: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  angle: number,
  length: number,
  width: number,
  stage: number,
  seed: number,
  isFork: boolean,
): void {
  if (length < 3) return;

  const segs = isFork ? 3 : 5 + Math.min(2, Math.floor(stage / 2));
  const wobbleAmp = ((seed * 13) % 5) / 5 * 0.18;
  let px = x0;
  let py = y0;

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (let i = 0; i < segs; i++) {
    const u = (i + 1) / segs;
    const a = angle + wobbleAmp * Math.sin(u * Math.PI * (1.5 + (seed % 3) * 0.3));
    const segLen = length / segs;
    const nx = px + Math.cos(a) * segLen;
    const ny = py + Math.sin(a) * segLen;
    const w = width * (1 - u * 0.35);

    // Shadow / depth
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.lineWidth = w + 1.2;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(nx, ny);
    ctx.stroke();

    // Dark core
    ctx.strokeStyle = 'rgba(12, 10, 8, 0.98)';
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(nx, ny);
    ctx.stroke();

    // Highlight edge
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)';
    ctx.lineWidth = Math.max(0.6, w * 0.28);
    ctx.beginPath();
    ctx.moveTo(px + 0.7, py + 0.7);
    ctx.lineTo(nx + 0.7, ny + 0.7);
    ctx.stroke();

    px = nx;
    py = ny;
  }
}

/** Map 0..1 mining progress → crack stage index. */
export function crackStageFromFraction(fraction: number): number {
  if (fraction <= 0.02) return -1;
  return Math.min(
    CRACK_STAGE_COUNT - 1,
    Math.floor(fraction * CRACK_STAGE_COUNT),
  );
}
